import React, { useCallback, useMemo, useState } from 'react';
import { ConversationCard } from '@televet/kibble-ui/build/patterns/ConversationCard';
import {
  ChannelListChannelFragment,
  ChannelViewChannelMemberFragment,
  GetClinicChannelViewChannelDocument,
} from 'shared/types/graphql';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { PanelViews } from 'shared/components/SidePanel/shared/types/enums/PanelViews';
import DevModeDisplayString from 'pages/Settings/components/DevModeDisplayString';
import { getChannelParticipants } from 'pages/Conversations/utils';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName, Placement } from 'shared/enums';
import CardMessage from './CardMessage';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Box } from '@televet/kibble-ui/build/chakra';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import useCheckChannelExportability, {
  CheckChannelExportabilityResponse,
} from 'pages/Conversations/hooks/useCheckChannelExportability';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { ExportType, openChannelOverlay, updateExportData } from 'pages/Conversations/state/conversationsSlice';
import usePinChannel from 'pages/Conversations/components/ChannelList/ChannelListItem/hooks/usePinChannel';
import useChannelTags from 'pages/Conversations/components/ChannelList/ChannelListItem/hooks/useChannelTags';
import ChannelAvatar from './ChannelAvatar';
import ChannelStatusSelect from 'shared/components/ChannelStatusSelect/ChannelStatusSelect';
import { usePetParentsWithDisplayName } from 'shared/hooks/usePetParentsWithDisplayName';
import { useApolloClient } from '@apollo/client';
import useClinicUser from 'shared/hooks/useClinicUser';

interface IProps {
  channel: ChannelListChannelFragment;
  isCurrentChannel: boolean;
  isLoadingNextPage: boolean;
  hasUnreadMessages: boolean;
  onChannelSelect(channelId: string): void;
  isChannelPinned?: boolean;
}

const ChannelListItem = ({
  channel,
  isCurrentChannel,
  isLoadingNextPage,
  hasUnreadMessages,
  onChannelSelect,
  isChannelPinned = false,
}: IProps): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { viewClinicPetParent } = useSidePanelSearch();
  const { currentView } = useSidePanel();
  const { currentClinicId, clinicUserId } = useClinicUser();
  const apolloClient = useApolloClient();

  const { channelPetParents, channelPetParentIds } = getChannelParticipants(
    channel.channelMembers as ChannelViewChannelMemberFragment[],
  );

  // Base
  const handleChannelSelect = useCallback(() => {
    const [clinicPetParentId] = channelPetParentIds;
    onChannelSelect(channel.id);

    if (clinicPetParentId && currentView !== PanelViews.Invoicing) {
      viewClinicPetParent({ clinicPetParentId, forceOpen: false });
    }
  }, [channel.id, channelPetParentIds, currentView, onChannelSelect, viewClinicPetParent]);

  // TimeStamp
  const channelUpdatedAtDate = useMemo(() => channel.updatedAt, [channel.updatedAt]);
  const lastMessageCreatedAtDate = useMemo(() => channel.lastMessage?.createdAt, [channel.lastMessage?.createdAt]);

  const sortByLastMessageEnabled = useMemo(
    () => isFeatureEnabled(FeatureFlagName.SortConversationByLastMessage) && lastMessageCreatedAtDate,
    [lastMessageCreatedAtDate, isFeatureEnabled],
  );

  // Header
  const { petParentsWithDisplayName } = usePetParentsWithDisplayName({
    petParents: channelPetParents,
  });

  const channelAvatars = useMemo(() => {
    return petParentsWithDisplayName.map(({ id, displayName, isInboundSms, isTest }) => (
      <ChannelAvatar
        key={`channelAvatar-${id}`}
        clinicPetParentId={id}
        displayName={displayName}
        isInboundSms={isInboundSms}
        isTest={isTest}
      />
    ));
  }, [petParentsWithDisplayName]);

  const firstPetParentId = useMemo(() => channelPetParents[0]?.id, [channelPetParents]);

  const petDisplayNames = useMemo(() => {
    return channel.pets.map(({ name }) => name).join(', ');
  }, [channel.pets]);

  // Header Menu
  const { checkChannelExportability } = useCheckChannelExportability();
  const overlayChannelId = useAppSelector((state) => state.conversations.channelViewData.channelId);
  const dispatch = useAppDispatch();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const [channelExportability, setChannelExportability] = useState<CheckChannelExportabilityResponse>();

  const fetchMenuData = useCallback(async () => {
    const exportability = await checkChannelExportability(channel.id);
    setChannelExportability(exportability);
  }, [checkChannelExportability, channel.id]);

  const openExportModal = useCallback(
    (exportType: ExportType) => {
      dispatch(
        updateExportData({
          isExportModalOpen: true,
          selectedExportType: exportType,
          channelPetParents,
          channelId: channel.id,
        }),
      );
    },
    [dispatch, channelPetParents, channel.id],
  );

  const handleOpenChannelOverlayClick = useCallback(() => {
    dispatch(openChannelOverlay({ channelId: channel.id }));
  }, [channel.id, dispatch]);

  // Pin
  const { togglePin } = usePinChannel({
    channelId: channel.id,
    isChannelPinned,
  });

  // Tags
  const { tags } = useChannelTags({
    channelId: channel.id,
    tags: channel.tags,
  });

  // Prefetch the channel view query on conversation card hover
  const prefetchChannelView = useCallback(() => {
    apolloClient.query({
      query: GetClinicChannelViewChannelDocument,
      variables: {
        channelId: channel.id,
        clinicId: currentClinicId,
        userId: clinicUserId,
      },
    });
  }, [apolloClient, channel.id, clinicUserId, currentClinicId]);

  return (
    <Box w="100%" onMouseEnter={prefetchChannelView} sx={{ overflowAnchor: 'none' }}>
      <ConversationCard
        onClick={handleChannelSelect}
        isActive={isCurrentChannel}
        showNotificationIndicator={hasUnreadMessages}
        shouldPreservePosition={isLoadingNextPage}
        headerProps={{
          title: petParentsWithDisplayName.map(({ displayName }) => displayName).join(', '),
          subtitle: petDisplayNames,
          timestampProps: {
            date: new Date(sortByLastMessageEnabled ? lastMessageCreatedAtDate : channelUpdatedAtDate),
          },
        }}
        headerMenuProps={{
          onOpen: fetchMenuData,
          listProps: {
            menuItems: [
              {
                label: (
                  <Tooltip
                    isDisabled={!channelExportability}
                    label={
                      channelExportability?.message || `Client and/or patient are not in ${primaryIntegrationName}`
                    }
                  >
                    <Box position="relative">
                      <Text size="sm">Export to {primaryIntegrationName}</Text>
                    </Box>
                  </Tooltip>
                ),
                isDisabled: !!channelExportability,
                value: 'ExportToPims',
                leftElement: <Icon size="md" name="upload" />,
                onSelect: (): void => {
                  openExportModal('ExportToPims');
                },
              },
              {
                label: 'Download PDF',
                value: 'DownloadPdf',
                leftElement: <Icon size="md" name="form" />,
                onSelect: (): void => {
                  openExportModal('DownloadPdf');
                },
              },
            ],
          },
        }}
        avatarGroupProps={{
          avatars: channelAvatars,
          max: 1,
          onAvatarGroupClick: (): void => {
            viewClinicPetParent({ clinicPetParentId: firstPetParentId || '' });
          },
        }}
        overlayProps={{
          isOpenInOverlay: overlayChannelId === channel.id,
          openInOverlay: handleOpenChannelOverlayClick,
        }}
        pinProps={{
          isPinned: isChannelPinned,
          togglePin,
        }}
        {...(channel.lastMessage && {
          content: <CardMessage lastChannelMessage={channel.lastMessage} channelId={channel.id} />,
        })}
        {...(channel.channelStatus && {
          contentMenu: (
            <ChannelStatusSelect
              currentStatus={channel.channelStatus}
              channelId={channel.id}
              placement={Placement.BottomEnd}
              showRightIcon={false}
            />
          ),
        })}
        tags={tags}
      />
      <DevModeDisplayString src={`src : ${channel.creationSource?.creationSource}`} />
    </Box>
  );
};

export default React.memo(ChannelListItem);
